import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';

import '@/i18n';

import { isInsideShopifyAdmin } from '@/lib/app-bridge';

import { ApiClientProvider } from '@/api/ApiClientProvider';
import { AccountProvider } from '@/auth/AccountProvider';
import { AuthProvider } from '@/auth/AuthProvider';
import { CarrierServiceGate } from '@/auth/CarrierServiceGate';
import { OnboardingGate } from '@/auth/OnboardingGate';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/app-layout';
import { LocaleSync } from '@/components/layout/locale-sync';
import { Toaster } from '@/components/ui/sonner';

import { LoginPage } from '@/pages/LoginPage';
import { ShopifyCallbackPage } from '@/pages/auth/ShopifyCallbackPage';
import { AuthorizationPage } from '@/pages/authorization/AuthorizationPage';
import { CarriersIndexPage } from '@/pages/carriers/CarriersIndexPage';
import { InPostCallbackPage } from '@/pages/connect/InPostCallbackPage';
import { SelectInPostOrganizationPage } from '@/pages/connect/SelectInPostOrganizationPage';
import { ExcludedProductsPage } from '@/pages/excluded-products/ExcludedProductsPage';
import { HelpPage } from '@/pages/help/HelpPage';
import { OrderDetailPage } from '@/pages/orders/OrderDetailPage';
import { OrderShopifyRedirectPage } from '@/pages/orders/OrderShopifyRedirectPage';
import { MarketsPage } from '@/pages/markets/MarketsPage';
import { CourierPickupsPage } from '@/pages/pickup-orders/CourierPickupsPage';
import { PlanRequiredPage } from '@/pages/plan/PlanRequiredPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { SetupWizardPage } from '@/pages/setup/SetupWizardPage';
import { ShipmentsPage } from '@/pages/shipments/ShipmentsPage';
import { FreeShippingPage } from '@/pages/widgets/FreeShippingPage';
import { OohPopupPage } from '@/pages/widgets/OohPopupPage';
import { PromiseDatePage } from '@/pages/widgets/PromiseDatePage';
import { WidgetsPage } from '@/pages/widgets/WidgetsPage';

/**
 * Root entry. A non-embedded launch from Shopify lands here whenever the configured
 * application_url has no path (the Shopify CLI rewrites it to the bare tunnel root on
 * `app dev`). Forward the signed `hmac/shop` params into the Shopify callback flow
 * instead of dropping them on the redirect to /shipments.
 */
function RootEntry() {
  const [params] = useSearchParams();

  if (!isInsideShopifyAdmin() && params.has('hmac') && params.has('shop')) {
    return <Navigate to={`/auth/shopify/callback?${params.toString()}`} replace />;
  }

  return <Navigate to="/shipments" replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      throwOnError: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AccountProvider>
            <ApiClientProvider>
              <LocaleSync />
              <Routes>
                <Route path="/" element={<RootEntry />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/shopify/callback" element={<ShopifyCallbackPage />} />

                <Route path="/connect/inpost/callback" element={<InPostCallbackPage />} />
                <Route path="/auth/inpost/callback" element={<InPostCallbackPage />} />

                <Route
                  path="/setup"
                  element={
                    <ProtectedRoute>
                      <SetupWizardPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/plan-required"
                  element={
                    <ProtectedRoute>
                      <PlanRequiredPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  element={
                    <ProtectedRoute>
                      <CarrierServiceGate>
                        <OnboardingGate>
                          <AppLayout />
                        </OnboardingGate>
                      </CarrierServiceGate>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/orders" element={<Navigate to="/shipments" replace />} />
                  <Route path="/orders/from-shopify" element={<OrderShopifyRedirectPage />} />
                  <Route path="/orders/:id" element={<OrderDetailPage />} />

                  <Route path="/shipments" element={<ShipmentsPage />} />

                  <Route path="/markets" element={<MarketsPage />} />
                  <Route path="/services" element={<Navigate to="/markets" replace />} />
                  <Route path="/excluded-products" element={<ExcludedProductsPage />} />
                  <Route path="/pickup-orders" element={<CourierPickupsPage />} />
                  <Route path="/widgets" element={<WidgetsPage />} />
                  <Route path="/widgets/free-shipping" element={<FreeShippingPage />} />
                  <Route path="/widgets/promise-date" element={<PromiseDatePage />} />
                  <Route path="/widgets/ooh-popup" element={<OohPopupPage />} />

                  <Route path="/carriers" element={<CarriersIndexPage />} />
                  <Route path="/authorization" element={<AuthorizationPage />} />
                  <Route
                    path="/connect/inpost/select-organization"
                    element={<SelectInPostOrganizationPage />}
                  />

                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/help" element={<HelpPage />} />

                  <Route
                    path="/connect/shopify"
                    element={<Navigate to="/authorization" replace />}
                  />
                  <Route
                    path="/connect/inpost"
                    element={<Navigate to="/authorization" replace />}
                  />
                  <Route path="/auth" element={<Navigate to="/authorization" replace />} />
                  <Route path="/auth/shopify" element={<Navigate to="/authorization" replace />} />
                  <Route path="/auth/inpost" element={<Navigate to="/authorization" replace />} />
                  <Route
                    path="/auth/inpost/select-organization"
                    element={<Navigate to="/connect/inpost/select-organization" replace />}
                  />
                </Route>

                <Route path="*" element={<Navigate to="/shipments" replace />} />
              </Routes>
              <Toaster />
            </ApiClientProvider>
          </AccountProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
